// Settings that every screen and the server agree on: exchange rates, the KPI targets ("acceptable rates") and the rule book.
import { z } from 'zod';

export type KpiGroup = 'Financial' | 'Agents' | 'Carriers';
export type KpiUnit = '%' | 'JOD' | 'JOD/kg' | 'JOD/CBM';

export type KpiDef = {
  id: string;
  label: string;
  group: KpiGroup;
  unit: KpiUnit;
  /** "higher" = the number should be at least the target; "lower" = at most the target */
  better: 'higher' | 'lower';
  /** the starting value, until Ibraheem sets his own in Settings */
  default: number;
  /** where it is shown */
  shownOn: string;
  help: string;
};

/** Every KPI that has an acceptable rate. This list is the KPI sheet. */
export const KPIS = [
  {
    id: 'avgMargin',
    label: 'Average profit margin',
    group: 'Financial',
    unit: '%',
    better: 'higher',
    default: 8,
    shownOn: 'Financial Overview, customer Financials',
    help: "The average of each shipment's own margin.",
  },
  {
    id: 'lowMargin',
    label: 'Low margin warning',
    group: 'Financial',
    unit: '%',
    better: 'higher',
    default: 5,
    shownOn: 'Financial Overview (Needs Attention)',
    help: 'A shipment under this margin is listed as a low margin shipment.',
  },
  {
    id: 'profitPerContainer',
    label: 'Average profit per container (FCL)',
    group: 'Financial',
    unit: 'JOD',
    better: 'higher',
    default: 300,
    shownOn: 'Financial Overview, customer Financials',
    help: 'Total FCL profit divided by containers.',
  },
  {
    id: 'profitPerKg',
    label: 'Average profit per KG (air freight)',
    group: 'Financial',
    unit: 'JOD/kg',
    better: 'higher',
    default: 0.3,
    shownOn: 'Financial Overview, customer Financials',
    help: 'Total air freight profit divided by chargeable kg.',
  },
  {
    id: 'profitPerCbm',
    label: 'Average profit per CBM (LCL)',
    group: 'Financial',
    unit: 'JOD/CBM',
    better: 'higher',
    default: 12,
    shownOn: 'Financial Overview, customer Financials',
    help: 'Total LCL profit divided by CBM.',
  },
  {
    id: 'responseRate',
    label: 'Agent response rate',
    group: 'Agents',
    unit: '%',
    better: 'higher',
    default: 80,
    shownOn: 'Agent page',
    help: 'Rates received divided by requests for rates sent.',
  },
  {
    id: 'winRate',
    label: 'Agent win rate',
    group: 'Agents',
    unit: '%',
    better: 'higher',
    default: 30,
    shownOn: 'Agents list, agent page',
    help: 'Rates used in a quotation divided by requests sent.',
  },
  {
    id: 'priceCompetitiveness',
    label: 'Price competitiveness (agents and carriers)',
    group: 'Agents',
    unit: '%',
    better: 'higher',
    default: 90,
    shownOn: 'Agent page, carrier page',
    help: 'Lowest rate on the same request divided by this rate (100% = cheapest).',
  },
  {
    id: 'transitReliability',
    label: 'Agent transit reliability',
    group: 'Agents',
    unit: '%',
    better: 'higher',
    default: 85,
    shownOn: 'Agent page',
    help: 'Delivered shipments that arrived by the promised date.',
  },
  {
    id: 'carrierReliability',
    label: 'Carrier reliability',
    group: 'Carriers',
    unit: '%',
    better: 'higher',
    default: 85,
    shownOn: 'Carriers list, carrier page',
    help: 'Shipments that ran on schedule: not delayed, not rolled over.',
  },
  {
    id: 'rolloverAcceptable',
    label: 'Acceptable rollover rate',
    group: 'Carriers',
    unit: '%',
    better: 'lower',
    default: 10,
    shownOn: 'Carriers list, carrier page',
    help: 'Rollover risk is Low below this share of bookings rolled over.',
  },
  {
    id: 'rolloverHigh',
    label: 'High rollover risk from',
    group: 'Carriers',
    unit: '%',
    better: 'lower',
    default: 25,
    shownOn: 'Carriers list, carrier page',
    help: 'Rollover risk is High from this share on; Medium in between.',
  },
  {
    id: 'delayedShare',
    label: 'Delayed shipments that need attention',
    group: 'Carriers',
    unit: '%',
    better: 'lower',
    default: 25,
    shownOn: 'Carriers (Needs Attention)',
    help: 'A carrier needs attention when this share of its shipments is delayed.',
  },
] as const satisfies readonly KpiDef[];

export type KpiId = (typeof KPIS)[number]['id'];
export const KPI_BY_ID = Object.fromEntries(KPIS.map((k) => [k.id, k])) as unknown as Record<KpiId, KpiDef>;

export type RuleGroup = 'Active or inactive' | 'Chasing' | 'Counting' | 'Claude';
export type RuleDef = {
  id: string;
  label: string;
  group: RuleGroup;
  unit: 'days' | 'bookings' | 'runs';
  default: number;
  help: string;
  /** where the app applies it */
  appliesTo: string;
};

/** Every timing rule in the rule book. Claude follows the same numbers once it is connected. */
export const RULES = [
  {
    id: 'customerInactiveDays',
    label: 'Customer becomes inactive after',
    group: 'Active or inactive',
    unit: 'days',
    default: 90,
    help: 'No shipment or request from them for this many days.',
    appliesTo: 'Customers',
  },
  {
    id: 'agentInactiveDays',
    label: 'Agent becomes inactive after',
    group: 'Active or inactive',
    unit: 'days',
    default: 90,
    help: 'No request, quote or shipment with them for this many days.',
    appliesTo: 'Overseas Agents',
  },
  {
    id: 'carrierInactiveDays',
    label: 'Carrier becomes inactive after',
    group: 'Active or inactive',
    unit: 'days',
    default: 90,
    help: 'No shipment or quote naming it for this many days.',
    appliesTo: 'Carriers',
  },
  {
    id: 'newWithinDays',
    label: 'Counts as new for',
    group: 'Active or inactive',
    unit: 'days',
    default: 30,
    help: 'A customer, agent or carrier is new this many days after its first shipment or request.',
    appliesTo: 'Customers, Agents, Carriers ("New" box)',
  },
  {
    id: 'chaseAgentDays',
    label: 'Chase an agent after',
    group: 'Chasing',
    unit: 'days',
    default: 2,
    help: 'Rates were requested and nothing has happened for this many days.',
    appliesTo: 'Shipments (Needs Attention)',
  },
  {
    id: 'chaseCustomerDays',
    label: 'Chase a customer after',
    group: 'Chasing',
    unit: 'days',
    default: 2,
    help: 'A quotation was sent and nothing has happened for this many days.',
    appliesTo: 'Shipments (Needs Attention)',
  },
  {
    id: 'minBookingsForRollover',
    label: 'Bookings needed to judge rollover risk',
    group: 'Counting',
    unit: 'bookings',
    default: 2,
    help: 'A carrier with fewer bookings shows "Not enough data" instead of a risk.',
    appliesTo: 'Carriers',
  },
  {
    id: 'claudeRunsPerHour',
    label: 'Automatic Claude runs per hour, at most',
    group: 'Claude',
    unit: 'runs',
    default: 6,
    help: 'Limits what wakes Claude by itself (new mail, its own reminders, the morning check) so it cannot use up your subscription. 0 = no limit. Anything you ask for by hand always runs at once.',
    appliesTo: 'The server (Claude on the server)',
  },
] as const satisfies readonly RuleDef[];

export type RuleId = (typeof RULES)[number]['id'];

const kpiNumber = z
  .number('Enter a number for each target.')
  .min(0, 'Targets cannot be negative.')
  .max(1_000_000, 'That target is too big.');
const ruleNumber = z
  .number('Enter a number for each rule.')
  .int('A rule counts whole days or bookings.')
  .min(0, 'A rule cannot be negative.')
  .max(3650, 'That number is too big.');
const rate = z
  .number('Enter an exchange rate.')
  .positive('An exchange rate must be more than 0.')
  .max(1000, 'That exchange rate is too big.');

/** The kinds of notification Ibraheem can switch on or off in Notifications */
export const NOTIFICATION_CATEGORIES = [
  { id: 'approvals', label: 'Approvals', help: 'Cards waiting for your decision' },
  { id: 'shipmentUpdates', label: 'Shipment Updates', help: 'ETA changes, arrivals, bookings' },
  { id: 'financialAlerts', label: 'Financial Alerts', help: 'Overdue invoices, missing costs' },
  { id: 'reminders', label: 'Reminders', help: 'Your reminders that are due soon' },
  { id: 'claudeErrors', label: 'Claude Errors', help: 'When something Claude did needs fixing' },
  { id: 'routineUpdates', label: 'Routine Updates', help: 'Checks and data synchronization' },
] as const;
export type NotificationCategoryId = (typeof NOTIFICATION_CATEGORIES)[number]['id'];

export type Settings = {
  /** how many JOD one unit of each currency is worth */
  fx: { USD: number; EUR: number };
  /** the acceptable rate (target) for each KPI */
  kpis: Record<KpiId, number>;
  /** the rule book: how many days or bookings each rule waits for */
  rules: Record<RuleId, number>;
  /** which kinds of notification are shown (and pushed, once each is connected) */
  notifications: Record<NotificationCategoryId, boolean>;
};

export const SettingsSchema = z.object({
  fx: z.object({ USD: rate, EUR: rate }),
  kpis: z.object(Object.fromEntries(KPIS.map((k) => [k.id, kpiNumber]))),
  rules: z.object(Object.fromEntries(RULES.map((r) => [r.id, ruleNumber]))),
  notifications: z.object(Object.fromEntries(NOTIFICATION_CATEGORIES.map((c) => [c.id, z.boolean()]))),
}) as unknown as z.ZodType<Settings>;

export const DEFAULT_SETTINGS: Settings = {
  fx: { USD: 0.709, EUR: 0.82 },
  kpis: Object.fromEntries(KPIS.map((k) => [k.id, k.default])) as Record<KpiId, number>,
  rules: Object.fromEntries(RULES.map((r) => [r.id, r.default])) as Record<RuleId, number>,
  // everything on except the routine checks
  notifications: {
    approvals: true,
    shipmentUpdates: true,
    financialAlerts: true,
    reminders: true,
    claudeErrors: true,
    routineUpdates: false,
  },
};

/** What was saved, filled in with defaults for anything missing (older data, newly added KPIs) */
export function mergeSettings(stored: unknown): Settings {
  const s = (stored ?? {}) as Partial<{
    fx: Partial<Settings['fx']>;
    kpis: Partial<Settings['kpis']>;
    rules: Partial<Settings['rules']>;
    notifications: Partial<Settings['notifications']>;
  }>;
  return {
    fx: { ...DEFAULT_SETTINGS.fx, ...s.fx },
    kpis: { ...DEFAULT_SETTINGS.kpis, ...s.kpis } as Record<KpiId, number>,
    rules: { ...DEFAULT_SETTINGS.rules, ...s.rules } as Record<RuleId, number>,
    notifications: { ...DEFAULT_SETTINGS.notifications, ...s.notifications },
  };
}

/** Is the actual number acceptable? (at least the target for "higher", at most for "lower") */
export const meetsKpi = (id: KpiId, actual: number, targets: Settings['kpis']) =>
  KPI_BY_ID[id].better === 'higher' ? actual >= targets[id] : actual <= targets[id];
